import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Avatar,
  IconButton
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const ProfilePhotoDialog = ({ open, onClose, onSave, currentPhoto }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);

  const compressImage = (file, maxSizeMB = 2, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        const maxDimension = 1200; // Maximum width or height
        let { width, height } = img;
        
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            // If compressed file is still too large, reduce quality
            if (blob.size > maxSizeMB * 1024 * 1024 && quality > 0.1) {
              compressImage(file, maxSizeMB, quality - 0.1).then(resolve);
            } else {
              // Create new file with original name but compressed data
              const compressedFile = new File([blob], file.name, {
                type: file.type,
                lastModified: Date.now()
              });
              resolve(compressedFile);
            }
          },
          file.type,
          quality
        );
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match('image.*')) {
      setError('Please select an image file (JPEG, PNG, etc.)');
      return;
    }

    // Validate file size (max 10MB before compression)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image size should be less than 10MB');
      return;
    }

    try {
      // Compress the image if it's larger than 2MB
      let processedFile = file;
      if (file.size > 2 * 1024 * 1024) {
        processedFile = await compressImage(file);
      }

      setSelectedFile(processedFile);
      setPreviewUrl(URL.createObjectURL(processedFile));
      setError(null);
    } catch (err) {
      console.error('Error processing image:', err);
      setError('Error processing image. Please try a different file.');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select an image first');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('profilePhoto', selectedFile);

      // Upload file to server
      const response = await api.put('/users/profile/photo', formData);

      const data = response.data;
      
      // Call the onSave callback with the server response
      onSave({
        profilePhoto: data.data.profilePhoto,
        profilePhotoThumbnail: data.data.profilePhotoThumbnail
      });
      
      // Reset state
      setSelectedFile(null);
      setPreviewUrl(null);
    } catch (err) {
      console.error('Error uploading profile photo:', err);
      setError(err.message || 'Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Profile Photo</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
          {previewUrl ? (
            <Box sx={{ position: 'relative', mb: 2 }}>
              <Avatar 
                src={previewUrl} 
                alt="Profile Preview" 
                sx={{ width: 200, height: 200 }}
              />
              <IconButton
                color="error"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  backgroundColor: 'white',
                  '&:hover': { backgroundColor: '#f5f5f5' }
                }}
                onClick={handleClearSelection}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ) : (
            <Avatar 
              src={currentPhoto} 
              alt="Current Profile" 
              sx={{ width: 200, height: 200, mb: 2 }}
            />
          )}

          {!previewUrl && (
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              disabled={isUploading}
            >
              Select Photo
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileSelect}
              />
            </Button>
          )}

          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isUploading}>
          Cancel
        </Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          disabled={!selectedFile || isUploading}
          startIcon={isUploading ? <CircularProgress size={20} /> : null}
        >
          {isUploading ? 'Uploading...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePhotoDialog;
